import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AlertCircle, ArrowLeft, Loader } from 'lucide-react';
import { LinkedInAd } from '../types/dataTypes';

const LinkedInAdsPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [ads, setAds] = useState<LinkedInAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from('linkedin_ads')
          .select('*')
          .eq('original_company_id', companyId);

        if (error) throw error;

        setAds(data || []);
      } catch (err) {
        console.error('Error fetching LinkedIn ads:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch ads');
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchAds();
    }
  }, [companyId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center h-64">
            <Loader className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="ml-3 text-gray-600 dark:text-gray-300">Loading ads...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="container mx-auto px-4">
          <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="text-red-500 dark:text-red-400" />
              <span className="ml-3 text-red-700 dark:text-red-300">{error}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          LinkedIn Ads for Company ID: {companyId}
        </h1>

        {ads.length === 0 ? (
          <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-4">
            <p className="text-yellow-700 dark:text-yellow-300">
              No LinkedIn ads found for this company ID.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ads.map((ad, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
              >
                {ad.imageURL && (
                  <img
                    src={ad.imageURL}
                    alt={ad.Headline || 'Ad preview'}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-4">
                  {ad.Headline && (
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {ad.Headline}
                    </h3>
                  )}
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                    {ad.Body}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {ad.ctas && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 text-xs rounded-full">
                        CTA: {ad.ctas}
                      </span>
                    )}
                    {ad.Format && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200 text-xs rounded-full">
                        {ad.Format}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default LinkedInAdsPage;